import type { EtapaDaNegociacao, Negociacao, ParcelaPontual } from './tipos';

/** O que está marcado no "Filtro" das negociações do Pontual. */
export interface EscolhasDoPontual {
  readonly etapas: readonly EtapaDaNegociacao[];
  readonly tipos: readonly string[];
}

export const FILTRO_DO_PONTUAL_VAZIO: EscolhasDoPontual = { etapas: [], tipos: [] };

/** Sem acento e em minúsculas: "Iguaçu" acha "IGUACU". */
function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** A busca: fornecedor, comprador, assessor ou o número do protocolo. */
export function naBusca(n: Negociacao, busca: string): boolean {
  const termo = normalizar(busca.trim());
  if (termo === '') return true;
  return [n.fornecedorNome, n.compradorNome, n.parceiroNome, n.protocoloTransferencia].some(
    (texto) => texto !== null && normalizar(texto).includes(termo),
  );
}

/**
 * A lista que a tabela mostra: a busca e o filtro. Dentro de um grupo do filtro
 * vale qualquer opção marcada (Paga **ou** Em atraso); entre os grupos, todos
 * juntos (Paga **e** Exportação).
 */
export function negociacoesNoFiltro(
  lista: readonly Negociacao[],
  escolhas: EscolhasDoPontual,
  busca: string,
): Negociacao[] {
  return lista.filter(
    (n) =>
      naBusca(n, busca) &&
      (escolhas.etapas.length === 0 || escolhas.etapas.includes(n.proximaAcao.etapa)) &&
      (escolhas.tipos.length === 0 || escolhas.tipos.includes(n.tipoCredito)),
  );
}

/** Uma parcela em aberto, com quem paga — uma linha do "A receber". */
export interface ParcelaAReceber {
  readonly parcela: ParcelaPontual;
  readonly negociacao: Negociacao;
  /** Quem deve: o comprador, no pagamento do crédito; o pagador dos honorários, nos honorários. */
  readonly pagador: string;
  readonly honorarios: boolean;
  readonly atrasada: boolean;
}

/**
 * As parcelas em aberto de todas as negociações vivas, da que vence primeiro
 * para a última — o que o cartão "A receber" soma, linha a linha.
 *
 * O crédito é pago pelo comprador; os honorários do assessor, por quem a
 * negociação diz que os paga (`honorariosPagador`, normalmente o fornecedor).
 */
export function parcelasAReceber(lista: readonly Negociacao[]): ParcelaAReceber[] {
  return lista
    .filter((n) => n.status !== 'CANCELADA')
    .flatMap((n) =>
      n.parcelasPagamento
        .filter((p) => p.status === 'PENDENTE' || p.status === 'ATRASADA')
        .map((p) => {
          const honorarios = p.natureza === 'HONORARIOS_ASSESSOR';
          const pagador = !honorarios
            ? n.compradorNome
            : n.honorariosPagador === 'COMPRADOR'
              ? n.compradorNome
              : n.fornecedorNome;
          return {
            parcela: p,
            negociacao: n,
            pagador: pagador ?? (honorarios ? 'Pagador dos honorários' : 'Comprador'),
            honorarios,
            atrasada: p.status === 'ATRASADA',
          };
        }),
    )
    .sort(
      (a, b) =>
        a.parcela.dataVencimento.localeCompare(b.parcela.dataVencimento) ||
        a.pagador.localeCompare(b.pagador),
    );
}

/** Quanto cada um deve, somando as parcelas em aberto — de quem deve mais para quem deve menos. */
export function porPagador(parcelas: readonly ParcelaAReceber[]): {
  readonly pagador: string;
  readonly total: number;
  readonly parcelas: number;
  /** O valor já vencido, dentro do total. */
  readonly emAtraso: number;
}[] {
  const somas = new Map<string, { total: number; parcelas: number; emAtraso: number }>();
  for (const p of parcelas) {
    const atual = somas.get(p.pagador) ?? { total: 0, parcelas: 0, emAtraso: 0 };
    somas.set(p.pagador, {
      total: atual.total + p.parcela.valor,
      parcelas: atual.parcelas + 1,
      emAtraso: atual.emAtraso + (p.atrasada ? p.parcela.valor : 0),
    });
  }
  return [...somas.entries()]
    .map(([pagador, s]) => ({
      pagador,
      parcelas: s.parcelas,
      total: Math.round(s.total * 100) / 100,
      emAtraso: Math.round(s.emAtraso * 100) / 100,
    }))
    .sort((a, b) => b.total - a.total);
}
